"use client";

import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import posthog from "posthog-js";
import { cn } from "@/lib/utils";
import {
  AlertTriangle,
  ShieldAlert,
  RefreshCw,
  Home,
  ChevronRight,
  Terminal,
  CircuitBoard,
  Activity,
  FolderLock,
  Eye,
  EyeOff,
  Copy,
  Check,
  Zap,
  ChevronDown,
  FileCode2,
  AlertOctagon,
} from "lucide-react";

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type ErrorTier = "critical" | "contextual" | "feature";

export interface ErrorLayoutProps {
  /** The error tier determines visual severity */
  tier: ErrorTier;
  /** Error title - Human readable */
  title: string;
  /** Error description - Human readable */
  description: string;
  /** Error object for diagnostics */
  error?: Error & { digest?: string };
  /** Callback to retry/reset the error state */
  onRetry?: () => void;
  /** Callback to navigate home */
  onGoHome?: () => void;
  /** Additional actions */
  actions?: ErrorAction[];
  /** Whether auto-save is enabled (shows reassurance badge) */
  showAutoSaveBadge?: boolean;
  /** Context string for PostHog tracking */
  trackingContext?: string;
  /** Custom icon override */
  icon?: React.ReactNode;
  /** Children for additional content */
  children?: React.ReactNode;
}

export interface ErrorAction {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "ghost";
  icon?: React.ReactNode;
}

// ============================================================================
// POSTHOG EVENT HELPERS - Standardized event taxonomy
// ============================================================================

interface ErrorEventProperties {
  error_id?: string;
  error_name?: string;
  error_message?: string;
  error_tier: ErrorTier;
  context: string;
  page: string;
  timestamp: string;
  retry_count?: number;
}

function captureErrorEvent(
  eventName: string,
  props: Partial<ErrorEventProperties>,
) {
  if (typeof window !== "undefined" && posthog) {
    posthog.capture(eventName, {
      ...props,
      page: props.page || window.location.pathname,
      timestamp: props.timestamp || new Date().toISOString(),
    });
  }
}

function captureErrorException(
  error: Error & { digest?: string },
  context: string,
  tier: ErrorTier,
) {
  if (typeof window !== "undefined" && posthog) {
    posthog.captureException(error, {
      $exception_type: error.name,
      $exception_message: error.message,
      $exception_stack: error.stack,
      error_id: error.digest,
      error_tier: tier,
      context,
      page: window.location.pathname,
      timestamp: new Date().toISOString(),
    });
  }
}

// ============================================================================
// UTILITY COMPONENTS
// ============================================================================

/** Scanline transition effect */
const ScanlineOverlay: React.FC<{ active: boolean }> = ({ active }) => {
  if (!active) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-50 overflow-hidden"
      aria-hidden="true"
    >
      <div className="error-scanline absolute inset-x-0 h-0.5 bg-gradient-to-b from-transparent via-rose-500/20 to-transparent" />
    </div>
  );
};

/** Auto-save reassurance badge */
const AutoSaveBadge: React.FC = () => (
  <div className="error-autosave-badge">
    <Check className="h-3.5 w-3.5 text-emerald-500" />
    <span className="error-autosave-badge-text">RECENT CHANGES AUTO-SAVED</span>
  </div>
);

/** Error ID ribbon at bottom */
const ErrorIdRibbon: React.FC<{
  errorId?: string;
}> = ({ errorId }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(() => {
    if (errorId) {
      navigator.clipboard.writeText(errorId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [errorId]);

  if (!errorId) return null;

  return (
    <div className="error-id-ribbon" aria-label="Error information">
      <div className="error-id-ribbon-content">
        <div className="flex items-center gap-3">
          <CircuitBoard className="h-4 w-4 text-zinc-600" />
          <div className="flex items-center gap-2">
            <span className="error-id-ribbon-label">Error ID:</span>
            <code className="error-id-ribbon-code">{errorId}</code>
          </div>
        </div>
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
        >
          {copied ? (
            <>
              <Check className="h-3 w-3" />
              <span>Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              <span>Copy</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

/** Debug HUD for development */
const DebugHUD: React.FC<{
  error?: Error & { digest?: string };
  visible: boolean;
}> = ({ error, visible }) => {
  if (!visible || !error) return null;

  return (
    <div className="error-debug-hud error-content-enter-delay-2">
      <div className="error-debug-hud-header">
        <Terminal className="h-4 w-4 text-rose-500" />
        <span className="error-debug-hud-title">DEBUG OUTPUT</span>
      </div>

      {/* Error Name */}
      <div className="mb-2">
        <span className="error-debug-hud-label">Exception</span>
        <p className="font-mono text-sm text-rose-400">{error.name}</p>
      </div>

      {/* Error Message */}
      <div className="mb-3">
        <span className="error-debug-hud-label">Message</span>
        <p className="error-debug-hud-value">{error.message}</p>
      </div>

      {/* Stack Trace */}
      {error.stack && (
        <div>
          <span className="error-debug-hud-label">Stack Trace</span>
          <pre className="error-debug-hud-stack">
            {error.stack.split("\n").map((line, i) => {
              const hasPath = line.includes(".tsx") || line.includes(".ts");
              const hasNode = line.includes("node_modules");

              return (
                <div
                  key={i}
                  className={cn(
                    hasNode && "error-stack-node-modules",
                    hasPath && !hasNode && "error-stack-path-ts",
                  )}
                >
                  {line}
                </div>
              );
            })}
          </pre>
        </div>
      )}
    </div>
  );
};

/** Dev mode toggle */
const DevModeToggle: React.FC<{
  enabled: boolean;
  onToggle: () => void;
}> = ({ enabled, onToggle }) => {
  if (process.env.NODE_ENV !== "development") return null;

  return (
    <button onClick={onToggle} className="error-dev-toggle">
      {enabled ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
      <span>{enabled ? "DEV VIEW" : "USER VIEW"}</span>
    </button>
  );
};

// ============================================================================
// TIER-SPECIFIC ICONS
// ============================================================================

const TierIcon: React.FC<{
  tier: ErrorTier;
  customIcon?: React.ReactNode;
}> = ({ tier, customIcon }) => {
  if (customIcon) {
    return <>{customIcon}</>;
  }

  const iconClass = "h-8 w-8";

  switch (tier) {
    case "critical":
      return <ShieldAlert className={cn(iconClass, "text-zinc-100")} />;
    case "contextual":
      return <AlertTriangle className={cn(iconClass, "text-zinc-300")} />;
    case "feature":
      return <Activity className={cn(iconClass, "text-zinc-400")} />;
    default:
      return <AlertTriangle className={cn(iconClass, "text-zinc-300")} />;
  }
};

// ============================================================================
// ACTION BUTTONS
// ============================================================================

const PrimaryActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ onClick, children, icon }) => (
  <button onClick={onClick} className="error-btn-primary">
    {icon}
    {children}
  </button>
);

const SecondaryActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ onClick, children, icon }) => (
  <button
    onClick={onClick}
    className={cn(
      "inline-flex items-center justify-center gap-2",
      "rounded-md px-4 py-2.5",
      "bg-zinc-900 text-zinc-300",
      "border border-zinc-800",
      "font-sans text-sm font-medium",
      "transition-all duration-200",
      "hover:border-zinc-700 hover:bg-zinc-800 hover:text-zinc-100",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-700 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    )}
  >
    {icon}
    {children}
  </button>
);

const GhostActionButton: React.FC<{
  onClick: () => void;
  children: React.ReactNode;
  icon?: React.ReactNode;
}> = ({ onClick, children, icon }) => (
  <button
    onClick={onClick}
    className={cn(
      "inline-flex items-center justify-center gap-2",
      "rounded-md px-4 py-2.5",
      "text-zinc-500",
      "font-sans text-sm font-medium",
      "transition-all duration-200",
      "hover:bg-zinc-900 hover:text-zinc-300",
      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-700 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    )}
  >
    {icon}
    {children}
  </button>
);

// ============================================================================
// ADMIN IDE STACK TRACE VIEW
// ============================================================================

/** Syntax-highlighted stack trace line */
function StackTraceLine({ line, index }: { line: string; index: number }) {
  const isNodeModules = line.includes("node_modules");
  const isTsxFile = line.includes(".tsx");
  const isTsFile = line.includes(".ts") && !isTsxFile;

  // Extract file path for highlighting
  const filePathMatch = line.match(/\(([^)]+)\)/);
  const filePath = filePathMatch ? filePathMatch[1] : null;

  if (isNodeModules) {
    return <div className="text-zinc-600 opacity-60">{line}</div>;
  }

  if ((isTsxFile || isTsFile) && filePath) {
    const beforePath = line.substring(0, line.indexOf("("));
    const afterPath = line.substring(line.indexOf(")") + 1);
    return (
      <div>
        <span className="text-zinc-400">{beforePath}</span>
        <span className="text-zinc-600">(</span>
        <span className="text-blue-400">{filePath}</span>
        <span className="text-zinc-600">)</span>
        <span className="text-zinc-400">{afterPath}</span>
      </div>
    );
  }

  if (isTsxFile || isTsFile) {
    return <div className="text-blue-400">{line}</div>;
  }

  // Error name/message at the top
  if (index === 0) {
    return <div className="font-medium text-rose-400">{line}</div>;
  }

  return <div className="text-zinc-400">{line}</div>;
}

/** IDE-style stack trace panel for admin errors */
export const AdminStackTracePanel: React.FC<{
  error: Error & { digest?: string };
}> = ({ error }) => {
  const [stackExpanded, setStackExpanded] = useState(true);
  const [copiedStack, setCopiedStack] = useState(false);

  const stackLines = error.stack?.split("\n") || [];

  const handleCopyStack = useCallback(() => {
    if (error.stack) {
      const debugInfo = `Error: ${error.name}\nMessage: ${error.message}\nDigest: ${error.digest || "N/A"}\n\nStack Trace:\n${error.stack}`;
      navigator.clipboard.writeText(debugInfo);
      setCopiedStack(true);
      setTimeout(() => setCopiedStack(false), 2000);
    }
  }, [error]);

  return (
    <div className="error-content-enter-delay-1 mb-6 overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950">
      {/* IDE Header */}
      <div className="flex items-center justify-between border-b border-zinc-800 bg-zinc-900/80 px-4 py-2">
        <div className="flex items-center gap-2">
          <Terminal className="h-4 w-4 text-rose-500" />
          <span className="font-mono text-xs font-medium tracking-wide text-zinc-300">
            error.log
          </span>
          <span className="rounded bg-rose-500/20 px-1.5 py-0.5 font-mono text-size-10 text-rose-400">
            EXCEPTION
          </span>
        </div>
        <button
          onClick={handleCopyStack}
          className="inline-flex items-center gap-1.5 rounded px-2 py-1 text-xs text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-300"
        >
          {copiedStack ? (
            <>
              <Check className="h-3 w-3" />
              <span>Copied</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3" />
              <span>Copy All</span>
            </>
          )}
        </button>
      </div>

      {/* Error Details */}
      <div className="p-4">
        {/* Error Name */}
        <div className="mb-3 flex items-start gap-3">
          <AlertOctagon className="mt-0.5 h-4 w-4 flex-shrink-0 text-rose-500" />
          <div className="flex-1">
            <span className="font-mono text-size-10 uppercase tracking-widest text-zinc-600">
              Exception Type
            </span>
            <p className="font-mono text-sm font-medium text-rose-400">
              {error.name}
            </p>
          </div>
        </div>

        {/* Error Message */}
        <div className="mb-4 flex items-start gap-3">
          <FileCode2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-zinc-600" />
          <div className="flex-1">
            <span className="font-mono text-size-10 uppercase tracking-widest text-zinc-600">
              Message
            </span>
            <p className="break-words font-mono text-xs leading-relaxed text-zinc-300">
              {error.message}
            </p>
          </div>
        </div>

        {/* Stack Trace - IDE Style */}
        {error.stack && (
          <div className="border-t border-zinc-800 pt-4">
            <button
              onClick={() => setStackExpanded(!stackExpanded)}
              className="mb-2 flex w-full items-center gap-2 text-left"
            >
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-zinc-600 transition-transform",
                  !stackExpanded && "-rotate-90",
                )}
              />
              <span className="font-mono text-size-10 uppercase tracking-widest text-zinc-600">
                Stack Trace
              </span>
              <span className="font-mono text-size-10 text-zinc-700">
                ({stackLines.length} lines)
              </span>
            </button>

            {stackExpanded && (
              <div className="overflow-hidden rounded border border-zinc-800 bg-zinc-900">
                {/* Line numbers gutter + code */}
                <div className="flex">
                  {/* Line numbers */}
                  <div className="flex-shrink-0 border-r border-zinc-800 bg-zinc-900/50 py-3 pl-3 pr-3 text-right">
                    {stackLines.map((_, i) => (
                      <div
                        key={i}
                        className="font-mono text-size-11 leading-relaxed text-zinc-700"
                      >
                        {i + 1}
                      </div>
                    ))}
                  </div>
                  {/* Code */}
                  <div className="flex-1 overflow-x-auto py-3 pl-3 pr-4">
                    <pre className="font-mono text-size-11 leading-relaxed">
                      {stackLines.map((line, i) => (
                        <StackTraceLine key={i} line={line} index={i} />
                      ))}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Digest */}
        {error.digest && (
          <div className="mt-4 flex items-center gap-2 border-t border-zinc-800 pt-4">
            <span className="font-mono text-size-10 uppercase tracking-widest text-zinc-600">
              Digest:
            </span>
            <code className="rounded bg-zinc-900 px-2 py-0.5 font-mono text-size-11 text-zinc-400">
              {error.digest}
            </code>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN BASE ERROR PAGE COMPONENT
// ============================================================================

export function BaseErrorPage({
  tier,
  title,
  description,
  error,
  onRetry,
  onGoHome,
  actions = [],
  showAutoSaveBadge = false,
  trackingContext = "error_page",
  icon,
  children,
}: ErrorLayoutProps) {
  const [showScanline, setShowScanline] = useState(true);
  const [devMode, setDevMode] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // Track error view and capture exception
  useEffect(() => {
    if (error) {
      captureErrorException(error, trackingContext, tier);
    }

    captureErrorEvent("error_page_viewed", {
      error_tier: tier,
      error_id: error?.digest,
      error_name: error?.name,
      error_message: error?.message,
      context: trackingContext,
    });

    // Console log for development
    if (error) {
      console.error(`[${trackingContext}] Error:`, error);
    }

    // Scanline effect on mount
    const timer = setTimeout(() => setShowScanline(false), 200);
    return () => clearTimeout(timer);
  }, [tier, error, trackingContext]);

  // Handle retry with PostHog tracking (for infinite loop detection)
  const handleRetry = useCallback(() => {
    const newCount = retryCount + 1;
    setRetryCount(newCount);

    captureErrorEvent("error_retry_clicked", {
      error_id: error?.digest,
      error_tier: tier,
      retry_count: newCount,
      context: trackingContext,
    });

    setShowScanline(true);
    setTimeout(() => {
      setShowScanline(false);
      onRetry?.();
    }, 150);
  }, [error, tier, trackingContext, retryCount, onRetry]);

  const handleGoHome = useCallback(() => {
    captureErrorEvent("error_go_home_clicked", {
      error_id: error?.digest,
      error_tier: tier,
      context: trackingContext,
    });
    onGoHome?.();
  }, [error, tier, trackingContext, onGoHome]);

  // Tier-specific container styles
  const containerStyles = cn(
    "relative flex min-h-screen flex-col items-center justify-center",
    "px-4 py-8 pb-20",
    tier === "critical" && "error-tier-critical",
    tier === "contextual" && "error-tier-contextual",
    tier === "feature" && "error-tier-feature",
  );

  return (
    <div className={containerStyles}>
      {/* Scanline transition */}
      <ScanlineOverlay active={showScanline} />

      {/* Circuit background pattern for critical errors */}
      {tier === "critical" && (
        <div
          className="error-circuit-bg pointer-events-none fixed inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
          aria-hidden="true"
        />
      )}

      {/* Content wrapper with max width */}
      <div
        className={cn(
          "error-content-enter relative z-10 w-full",
          tier === "critical" ? "max-w-md" : "max-w-lg",
          "text-center",
        )}
      >
        {/* Auto-save badge (for contextual errors) */}
        {showAutoSaveBadge && tier === "contextual" && (
          <div className="mb-6 flex justify-center">
            <AutoSaveBadge />
          </div>
        )}

        {/* Icon container */}
        <div
          className={cn(
            "mx-auto mb-6 flex items-center justify-center",
            "h-16 w-16 rounded-full",
            tier === "critical" && "bg-zinc-900 ring-1 ring-rose-500/30",
            tier === "contextual" && "bg-zinc-900/80",
            tier === "feature" && "bg-zinc-900/60",
          )}
        >
          <TierIcon tier={tier} customIcon={icon} />
        </div>

        {/* Title - Geist Sans Bold */}
        <h1
          className={cn(
            "mb-3 font-sans font-bold tracking-tight",
            tier === "critical" && "text-2xl text-zinc-100",
            tier === "contextual" && "text-xl text-zinc-100",
            tier === "feature" && "text-lg text-zinc-200",
          )}
        >
          {title}
        </h1>

        {/* Description - Geist Sans Regular */}
        <p className="mb-8 font-sans text-sm leading-relaxed text-zinc-400">
          {description}
        </p>

        {/* Action buttons */}
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          {onRetry && (
            <PrimaryActionButton
              onClick={handleRetry}
              icon={<RefreshCw className="h-4 w-4" />}
            >
              Try Again
            </PrimaryActionButton>
          )}

          {onGoHome && (
            <SecondaryActionButton
              onClick={handleGoHome}
              icon={<Home className="h-4 w-4" />}
            >
              Go to Dashboard
            </SecondaryActionButton>
          )}

          {actions.map((action, index) => {
            const ActionButton =
              action.variant === "ghost"
                ? GhostActionButton
                : action.variant === "secondary"
                  ? SecondaryActionButton
                  : PrimaryActionButton;

            return (
              <ActionButton
                key={index}
                onClick={action.onClick}
                icon={action.icon}
              >
                {action.label}
              </ActionButton>
            );
          })}
        </div>

        {/* Additional children content */}
        {children}

        {/* Debug HUD */}
        <DebugHUD error={error} visible={devMode} />
      </div>

      {/* Dev mode toggle */}
      <DevModeToggle enabled={devMode} onToggle={() => setDevMode(!devMode)} />

      {/* Error ID ribbon */}
      <ErrorIdRibbon errorId={error?.digest} />
    </div>
  );
}

// ============================================================================
// TIER C: FEATURE GHOST BOX (UI only - use existing boundary from custom/)
// ============================================================================

/** Ghost Box fallback UI for feature-level errors */
export const FeatureGhostBox: React.FC<{
  featureName: string;
  onReload: () => void;
  height?: string;
}> = ({ featureName, onReload, height = "200px" }) => (
  <div className="error-feature-ghost" style={{ minHeight: height }}>
    <Zap className="mb-3 h-5 w-5 text-zinc-600" />
    <p className="error-feature-ghost-title">{featureName} unavailable</p>
    <p className="error-feature-ghost-desc">
      This component encountered an error
    </p>
    <button onClick={onReload} className="error-feature-ghost-btn">
      <RefreshCw className="h-3 w-3" />
      Reload Component
    </button>
  </div>
);

// ============================================================================
// SPECIALIZED ERROR PAGE VARIANTS
// ============================================================================

/** Critical/Global error page */
export function CriticalErrorPage({
  error,
  onRetry,
  onGoHome,
}: {
  error?: Error & { digest?: string };
  onRetry?: () => void;
  onGoHome?: () => void;
}) {
  return (
    <BaseErrorPage
      tier="critical"
      title="System Error"
      description="A critical error has occurred. The system is attempting to recover. Please try again or return to the dashboard."
      error={error}
      onRetry={onRetry}
      onGoHome={onGoHome}
      trackingContext="critical_error"
    />
  );
}

/** Editor/Project error page with auto-save reassurance */
export function EditorErrorPage({
  error,
  onRetry,
  onGoHome,
  onReportIssue,
}: {
  error?: Error & { digest?: string };
  onRetry?: () => void;
  onGoHome?: () => void;
  onReportIssue?: () => void;
}) {
  return (
    <BaseErrorPage
      tier="contextual"
      title="Editor Error"
      description="The video editor encountered an unexpected error. Your recent changes have been preserved. Please try again to continue editing."
      error={error}
      onRetry={onRetry}
      onGoHome={onGoHome}
      showAutoSaveBadge={true}
      trackingContext="editor_error"
    >
      {onReportIssue && (
        <div className="mt-6">
          <button
            onClick={onReportIssue}
            className="font-sans text-sm text-zinc-500 underline underline-offset-4 transition-colors hover:text-zinc-300"
          >
            Report this issue
          </button>
        </div>
      )}
    </BaseErrorPage>
  );
}

/** Project error page with auto-save reassurance */
export function ProjectErrorPage({
  error,
  onRetry,
  onGoHome,
  onGoBack,
  onReportIssue,
}: {
  error?: Error & { digest?: string };
  onRetry?: () => void;
  onGoHome?: () => void;
  onGoBack?: () => void;
  onReportIssue?: () => void;
}) {
  const actions: ErrorAction[] = [];

  if (onGoBack) {
    actions.push({
      label: "Go Back",
      onClick: onGoBack,
      variant: "ghost",
    });
  }

  return (
    <BaseErrorPage
      tier="contextual"
      title="Project Error"
      description="Something went wrong while loading or running the project editor. Your work has been preserved. Please try again to continue working."
      error={error}
      onRetry={onRetry}
      onGoHome={onGoHome}
      actions={actions}
      showAutoSaveBadge={true}
      trackingContext="project_error"
    >
      {onReportIssue && (
        <div className="mt-6">
          <button
            onClick={onReportIssue}
            className="font-sans text-sm text-zinc-500 underline underline-offset-4 transition-colors hover:text-zinc-300"
          >
            Report this issue
          </button>
        </div>
      )}
    </BaseErrorPage>
  );
}

/** Admin error page with enhanced debugging */
export function AdminErrorPage({
  error,
  onRetry,
  onGoHome,
}: {
  error?: Error & { digest?: string };
  onRetry?: () => void;
  onGoHome?: () => void;
}) {
  return (
    <BaseErrorPage
      tier="contextual"
      title="Admin Panel Error"
      description="An error occurred in the admin panel. This has been logged for investigation."
      error={error}
      onRetry={onRetry}
      onGoHome={onGoHome}
      trackingContext="admin_error"
      icon={<ShieldAlert className="h-8 w-8 text-zinc-100" />}
    >
      {/* IDE-style stack trace panel */}
      {error && <AdminStackTracePanel error={error} />}
    </BaseErrorPage>
  );
}

/** Authenticated area error page */
export function AuthenticatedErrorPage({
  error,
  onRetry,
  onGoHome,
  onSignOut,
}: {
  error?: Error & { digest?: string };
  onRetry?: () => void;
  onGoHome?: () => void;
  onSignOut?: () => void;
}) {
  const actions: ErrorAction[] = [];

  if (onSignOut) {
    actions.push({
      label: "Sign Out",
      onClick: onSignOut,
      variant: "ghost",
      icon: <Activity className="h-4 w-4" />,
    });
  }

  return (
    <BaseErrorPage
      tier="contextual"
      title="Something Went Wrong"
      description="We encountered an unexpected error. This might be a temporary issue — please try again or sign out and back in if the problem persists."
      error={error}
      onRetry={onRetry}
      onGoHome={onGoHome}
      actions={actions}
      trackingContext="authenticated_error"
    />
  );
}

/** 404 Not Found with permission-aware messaging */
export function NotFoundPage({
  variant = "missing",
  onGoHome,
  onSwitchAccount,
}: {
  variant?: "missing" | "access_denied";
  onGoHome?: () => void;
  onSwitchAccount?: () => void;
}) {
  const isMissing = variant === "missing";

  const actions: ErrorAction[] = [];

  if (onSwitchAccount) {
    actions.push({
      label: "Switch Account",
      onClick: onSwitchAccount,
      variant: "secondary",
      icon: <ChevronRight className="h-4 w-4" />,
    });
  }

  return (
    <BaseErrorPage
      tier="contextual"
      title={isMissing ? "Project Not Found" : "Access Denied"}
      description={
        isMissing
          ? "This project doesn't exist or may have been deleted. Check the URL or return to your projects."
          : "You don't have permission to access this project. Try switching accounts or contact the project owner."
      }
      onGoHome={onGoHome}
      actions={actions}
      trackingContext={isMissing ? "not_found" : "access_denied"}
      icon={<FolderLock className="h-8 w-8 text-zinc-300" />}
    />
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
  ScanlineOverlay,
  AutoSaveBadge,
  ErrorIdRibbon,
  DebugHUD,
  DevModeToggle,
  PrimaryActionButton,
  SecondaryActionButton,
  GhostActionButton,
  StackTraceLine,
  captureErrorEvent,
  captureErrorException,
};
